/**
 * LiveTimer wrapper (BUILD-SPEC.md §5).
 *
 * `LiveTimer` stops ticking at zero; this app must not. This file is where
 * that gap is closed: it derives the current turn's "zero point" timestamp
 * from the `finished` event's `ITimerConfig`, and guards that derivation so
 * only the one client that actually owns the current turn's timer segment
 * acts on it.
 *
 * Why a guard is needed (BUILD-SPEC §5c): `finished` carries no `local`
 * flag, so it fires on *every* connected screen at (approximately) the same
 * moment. If every screen reacted by writing `LiveState`, that is a write
 * storm — N clients racing to `set()` the same object. This class does not
 * write shared state itself (that stays in session.ts, the only place that
 * touches `LiveState`); it only decides, locally, whether *this* client is
 * the one that should be allowed to.
 *
 * Chosen guard: track `local` on the `started`/`played` events — Live
 * Share's own signal for "did *I* cause this" — rather than comparing raw
 * `clientId`s by hand. This is the same idea the spec suggests ("compare
 * against the client that started the turn via config.clientId") but uses
 * the SDK-provided boolean instead of reimplementing identity comparison,
 * and is robust to the exact string shape of `clientId`. A second guard
 * (`configChangedAt` matching the segment we actually started) protects
 * against a stale `finished` arriving for a turn that has already been
 * superseded by a new `start()` (e.g. Next pressed right as the clock hit
 * zero).
 */
import type { LiveTimer } from "@microsoft/live-share";

/** BUILD-SPEC §5: "Set tickRate to ~200ms. Never derive state from tick
 * counts." — ticks are for cosmetic re-render pulses only. */
export const TICK_RATE_MS = 200;

/**
 * Shape of `ITimerConfig`, verified against @microsoft/live-share@1.4.2 in
 * BUILD-SPEC §5. Declared locally (rather than imported) since the spec
 * documents the *shape*, not a confirmed exported type name, and the
 * package's types were not available to check directly in this workspace
 * (dependencies are not installed — see final report).
 */
export interface TimerConfig {
  duration: number;
  position: number;
  running: boolean;
  configChangedAt: number;
  clientId: string;
}

/**
 * The turn's zero-crossing timestamp on the Live Share clock, in SECONDS —
 * BUILD-SPEC §5's "single most important detail". Every screen that runs
 * this over the same `config` gets the same answer, because
 * `configChangedAt`, `duration` and `position` are all part of the shared,
 * replicated timer state, not anything locally computed.
 *
 * `configChangedAt`/`duration`/`position` are all in milliseconds (real
 * `LiveTimer` fields — `duration` is what was passed to `start(durationMs)`).
 * `Session.zeroPointAt` is not: it sits alongside `turnStartedAt` and
 * `spentSeconds`, which are seconds throughout (see `src/live/client.ts`'s
 * `now`). The `/ 1000` below is that conversion, made once, at the one
 * place this value is computed — get it wrong and overtime doesn't just
 * look a little off, it explodes almost instantly, since every real
 * millisecond gets counted as a full second.
 */
export function zeroPointFromConfig(config: TimerConfig): number {
  return (config.configChangedAt + (config.duration - config.position)) / 1000;
}

/**
 * Wraps a single shared `LiveTimer` instance for the lifetime of the
 * session. There is one `LiveTimer` object for the whole standup; per
 * BUILD-SPEC §5 there is no `stop()`/`reset()`, so a hand-off (or any change
 * that moves the turn's zero-crossing — see session.ts's `restartFor`) is
 * represented by calling `start()` again rather than by creating a new
 * timer object.
 */
export class TurnTimer {
  private ownsCurrentSegment = false;
  private startedConfigChangedAt: number | null = null;
  private finishHandlers: Array<(zeroPointAt: number, config: TimerConfig) => void> = [];
  private tickHandlers: Array<(milliRemaining: number) => void> = [];

  constructor(private readonly timer: LiveTimer) {
    this.timer.tickRate = TICK_RATE_MS;

    const onSegmentStart = (config: TimerConfig, local: boolean): void => {
      this.ownsCurrentSegment = local;
      this.startedConfigChangedAt = config.configChangedAt;
    };

    // `started`/`played` both begin a new timer segment; `paused` never
    // does (pausing/resuming does not move the zero-crossing on its own —
    // see session.ts, which restarts the timer explicitly on resume so the
    // pause duration doesn't shift anything unexpectedly).
    this.timer.on("started", onSegmentStart);
    this.timer.on("played", onSegmentStart);

    this.timer.on("finished", (config: TimerConfig) => {
      const isStaleSegment = config.configChangedAt !== this.startedConfigChangedAt;
      if (!this.ownsCurrentSegment || isStaleSegment) {
        return; // guard: not my segment, or superseded by a later start().
      }
      const zeroPointAt = zeroPointFromConfig(config);
      this.finishHandlers.forEach((h) => h(zeroPointAt, config));
    });

    this.timer.on("onTick", (milliRemaining: number) => {
      this.tickHandlers.forEach((h) => h(milliRemaining));
    });
  }

  /**
   * (Re)starts the shared timer for the current turn, in milliseconds. Used
   * for every hand-off (start/next), and also — deliberately going beyond
   * the spec's literal "on hand-off" wording — for resume and extend, since
   * either can move the turn's zero-crossing and only a fresh `start()`
   * updates the config `finished` will eventually report. See session.ts's
   * `restartFor` for the single call site that computes the right duration.
   */
  start(durationMs: number): void {
    this.timer.start(Math.max(0, Math.round(durationMs)));
  }

  /** Freezes the shared timer in place; does not move the zero-crossing. */
  pause(): void {
    this.timer.pause();
  }

  /** Fires exactly once per turn, and only on the single client that owns
   * that turn's timer segment (see class doc for the two-part guard). */
  onOwnedFinish(handler: (zeroPointAt: number, config: TimerConfig) => void): () => void {
    this.finishHandlers.push(handler);
    return () => {
      this.finishHandlers = this.finishHandlers.filter((h) => h !== handler);
    };
  }

  /** Cosmetic pulse only — BUILD-SPEC: never derive state from tick counts. */
  onTick(handler: (milliRemaining: number) => void): () => void {
    this.tickHandlers.push(handler);
    return () => {
      this.tickHandlers = this.tickHandlers.filter((h) => h !== handler);
    };
  }
}
